import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/common/Input'
import { Label } from '@/ui/components/common/Label'
import { SocialLoginButtons } from '@/ui/components/public/SocialLoginButtons'
import type { User } from '@/domains/types'

type LoginResponse =
  | {
      requiresTwoFactor: false
      accessToken: string
      refreshToken: string
      user: Omit<User, 'createdAt' | 'updatedAt'>
    }
  | { requiresTwoFactor: true; tempToken: string }

const makeLoginSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    username: z.string().min(1, translate('validation.required')),
    password: z.string().min(1, translate('validation.required')),
  })

type LoginFormData = z.infer<ReturnType<typeof makeLoginSchema>>

export interface CredentialsSectionProps {
  /** Called when the API returns an `accessToken` and a fully resolved user. */
  onAuthenticated: (
    user: Omit<User, 'createdAt' | 'updatedAt'>,
    accessToken: string,
    refreshToken: string,
  ) => void
  /** Called when the API requires a TOTP step; receives the short-lived temp token. */
  onTwoFactorRequired: (tempToken: string) => void
}

/**
 * Step 1 of the public login flow: username/password form plus the OAuth/SSO
 * buttons (rendered with the public-context callback). Owns the credentials
 * form state, validation schema and submit handler. Notifies the parent page
 * either of full authentication or that a TOTP step is now required.
 */
export function CredentialsSection({
  onAuthenticated,
  onTwoFactorRequired,
}: CredentialsSectionProps) {
  const { t } = useTranslation()
  const loginSchema = useMemo(() => makeLoginSchema(t), [t])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) })

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', {
        username: data.username,
        password: data.password,
      })

      if (response.data.requiresTwoFactor) {
        onTwoFactorRequired(response.data.tempToken)
        return
      }
      onAuthenticated(response.data.user, response.data.accessToken, response.data.refreshToken)
    } catch (error: unknown) {
      const apiMessage = handleApiError(error)
      const isPending = apiMessage.toLowerCase().includes('awaiting approval')
      setError('root', {
        message: isPending ? t('validation.account_pending') : t('common.error'),
      })
    }
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      {errors.root && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-3 py-2">
          {errors.root.message}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="username">{t('auth.username')}</Label>
          <Input id="username" {...register('username')} className="mt-1" autoComplete="username" />
          {errors.username && (
            <p className="text-destructive text-xs mt-1">{errors.username.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="password">{t('auth.password')}</Label>
          <Input
            id="password"
            type="password"
            {...register('password')}
            className="mt-1"
            autoComplete="current-password"
          />
          {errors.password && (
            <p className="text-destructive text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          {t('auth.login_button')}
        </Button>
      </form>

      {/* Social login */}
      <SocialLoginButtons mode="login" />
    </div>
  )
}
